import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Pizza, LogOut, Store, Zap, Clock, Tag, PlayCircle, User } from "lucide-react";
import ProductsGrid from "@/components/ProductsGrid";
import MasterCatalog from "@/components/MasterCatalog";
import PromotionsSchedule from "@/components/PromotionsSchedule";
import AddonsMatrix from "@/components/AddonsMatrix";
import SitesManager from "@/components/SitesManager";
import HistoryPanel from "@/components/HistoryPanel";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [sites, setSites] = useState([]);
  const [activeSite, setActiveSite] = useState(null);
  const [status, setStatus] = useState(null);
  const [loadingSites, setLoadingSites] = useState(true);

  const loadSites = useCallback(async () => {
    try {
      const r = await api.get("/sites");
      setSites(r.data);
      setActiveSite((prev) => {
        if (prev && r.data.find((s) => s.id === prev.id)) return r.data.find((s) => s.id === prev.id);
        return r.data[0] || null;
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoadingSites(false);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    if (!activeSite) return;
    try {
      const r = await api.get(`/sites/${activeSite.id}/promotions/status`);
      setStatus(r.data);
    } catch (e) {
      /* ignore */
    }
  }, [activeSite]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);
  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const runScheduler = async () => {
    try {
      const r = await api.post("/scheduler/run");
      toast.success(r.data.message);
      setTimeout(loadStatus, 15000);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const activeCount = status?.promotions?.filter((p) => p.active).length || 0;
  const liveWindows = status?.promotions?.filter((p) => p.in_window && p.enabled) || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="px-4 md:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center shrink-0">
              <Pizza className="w-5 h-5 text-white" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="font-heading font-semibold text-sm" data-testid="header-brand-right">Kenny's Pizza</span>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" data-testid="user-menu-button">
                  <User className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-2 py-1.5 text-xs text-muted-foreground">{user?.email}</div>
                <DropdownMenuItem onClick={logout} data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Stats bar */}
      <div className="px-4 md:px-6 lg:px-8 py-4 border-b border-border bg-card/40">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={<Zap className="w-4 h-4" />}
            label="Active Promotions"
            value={activeCount}
            accent="text-primary"
            testid="stat-active-promos"
          />
          <StatCard
            icon={<Clock className="w-4 h-4" />}
            label="Live Windows Now"
            value={liveWindows.length}
            accent="text-emerald-400"
            testid="stat-live-windows"
          />
          <StatCard
            icon={<Tag className="w-4 h-4" />}
            label="Store Timezone"
            value={status?.timezone || activeSite?.timezone || "—"}
            accent="text-amber-400"
            small
            testid="stat-timezone"
          />
          <div className="flex items-center justify-end">
            <Button onClick={runScheduler} variant="secondary" className="gap-2" data-testid="run-scheduler-button">
              <PlayCircle className="w-4 h-4" /> Run Scheduler Now
            </Button>
          </div>
        </div>
        {liveWindows.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="live-windows-list">
            {liveWindows.map((w) => (
              <Badge key={w.id} className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 border gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {w.name} active
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Main content */}
      <main className="px-4 md:px-6 lg:px-8 py-6">
        {loadingSites ? (
          <p className="text-muted-foreground">Loading sites…</p>
        ) : !activeSite ? (
          <div className="text-center py-20">
            <Store className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No sites yet. Add a WooCommerce site to get started.</p>
            <div className="mt-6 max-w-2xl mx-auto">
              <SitesManager sites={sites} onChange={loadSites} />
            </div>
          </div>
        ) : (
          <Tabs defaultValue="master" className="w-full">
            <div className="mb-6 flex flex-wrap items-center gap-3">
            <TabsList className="max-w-full overflow-x-auto justify-start">
              <TabsTrigger value="master" data-testid="tab-master">
                All Sites
              </TabsTrigger>
              <TabsTrigger value="products" data-testid="tab-products">
                Products & Prices
              </TabsTrigger>
              <TabsTrigger value="promotions" data-testid="tab-promotions">
                Promotions
              </TabsTrigger>
              <TabsTrigger value="addons" data-testid="tab-addons">
                Add-Ons
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">
                History
              </TabsTrigger>
              <TabsTrigger value="sites" data-testid="tab-sites">
                Sites
              </TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Store for single-store tabs:</span>
              <Select
                value={activeSite?.id || ""}
                onValueChange={(v) => setActiveSite(sites.find((s) => s.id === v))}
              >
                <SelectTrigger className="w-[220px] md:w-[260px]" data-testid="site-selector-dropdown">
                  <Store className="w-4 h-4 text-muted-foreground shrink-0" />
                  <SelectValue placeholder="Select a site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`site-option-${s.id}`}>
                      <span className="flex items-center gap-2">
                        {s.name}
                        {s.demo && <span className="text-[10px] text-amber-400">DEMO</span>}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            </div>

            <TabsContent value="master">
              <MasterCatalog sites={sites} />
            </TabsContent>
            <TabsContent value="products">
              <ProductsGrid site={activeSite} />
            </TabsContent>
            <TabsContent value="promotions">
              <PromotionsSchedule site={activeSite} onChanged={loadStatus} />
            </TabsContent>
            <TabsContent value="addons">
              <AddonsMatrix site={activeSite} />
            </TabsContent>
            <TabsContent value="history">
              <HistoryPanel sites={sites} />
            </TabsContent>
            <TabsContent value="sites">
              <SitesManager sites={sites} activeSite={activeSite} onChange={loadSites} />
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, accent, small, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 flex items-center gap-3" data-testid={testid}>
      <div className={`w-9 h-9 rounded-md bg-secondary flex items-center justify-center ${accent}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        <p className={`font-heading font-bold ${small ? "text-sm font-mono-num" : "text-2xl"} ${accent}`}>{value}</p>
      </div>
    </div>
  );
}
